import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) { setError("Passwords do not match"); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated. Please sign in.");
            navigate("/login");
        } catch (err) {
            setError(fmtErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm animate-fade-up" data-testid="reset-password-card">
                <h2 className="font-heading text-2xl font-bold text-slate-900">Set new password</h2>
                <p className="mt-1 text-sm text-slate-500">Choose a strong new password for your account</p>
                {!token && <p data-testid="reset-no-token" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">This reset link is missing its token. Please use the link from your email.</p>}
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="space-y-1.5">
                        <Label>New Password</Label>
                        <Input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Confirm Password</Label>
                        <Input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" />
                    </div>
                    {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
                    <Button data-testid="reset-submit-btn" type="submit" disabled={loading || !token} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update Password
                    </Button>
                </form>
                <Link to="/login" data-testid="back-to-login-link" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#C2410C] hover:underline">
                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                </Link>
            </div>
        </div>
    );
}
